"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { useFetchAvatarObjections } from "@/app/hooks/fetchs/client-avatar/useFetchAvatarTabs";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import styles from "../AvatarDetail.module.css";
import { AvatarDetailCard } from "./AvatarDetailCard/AvatarDetailCard";

type Props = DefaultProps & {
  avatar: UserClientAvatar;
  isLoading?: boolean;
  isAnalyzing?: boolean;
  params: { lang: string };
};

const listPlaceholder = [
  { cells: [{ width: "90%" }] },
  { cells: [{ width: "80%" }] },
  { cells: [{ width: "65%" }] },
  { cells: [{ width: "75%" }] },
];

export default function AvatarDetailObjections(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const { avatar, isLoading, isAnalyzing = false, params } = props;
  const user = useAuthUser();
  const isPlaceholder = !user;
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: objectionsData, isLoading: isFetching } = useFetchAvatarObjections({
    avatarId: avatar.id,
    enabled: !isPlaceholder,
  });

  const fullAvatar = {
    ...avatar,
    ...(objectionsData || {}),
  } as UserClientAvatar;
  const isLoadingTab = isLoading || isFetching;

  const handleSaveList = async (fieldKey: AvatarField, newItems: string[]) => {
    try {
      await updateAvatarField(avatar.offerId, fieldKey, newItems);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.OBJECTIONS,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  if (isLoadingTab) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader
          centered
          delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
          color="var(--color-green-3)"
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_OBJECTIONS_BELIEFS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.falseBelief || []}
              onSave={(items) => handleSaveList(AvatarField.FALSE_BELIEF, items)}
              bulletColor="var(--color-analysis-objection)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_OBJECTIONS_PURCHASE)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.purchaseObjections || []}
              onSave={(items) => handleSaveList(AvatarField.PURCHASE_OBJECTIONS, items)}
              bulletColor="var(--color-analysis-objection)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <AvatarDetailCard
        title={T(TKey.CLIENT_AVATAR_DETAIL_OBJECTIONS_HANDLER)}
        isPlaceholder={isPlaceholder}
        placeholderConfig={[
          { cells: [{ width: "95%" }] },
          { cells: [{ width: "85%" }] },
          { cells: [{ width: "90%" }] },
          { cells: [{ width: "70%" }] },
        ]}
      >
        {isPlaceholder ? null : (
          <EditableList
            items={fullAvatar.objectionHandler || []}
            onSave={(items) => handleSaveList(AvatarField.OBJECTION_HANDLER, items)}
            bulletColor="var(--color-analysis-signal)"
            itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
            disabled={isAnalyzing || !canEdit}
          />
        )}
      </AvatarDetailCard>

      {isPlaceholder && (
        <div className={styles.ctaContainer}>
          <Button
            min={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_CREATE_BUTTON)}
            onClick={() => ModalStore.getState().openSignIn(params)}
          />
        </div>
      )}
    </div>
  );
}
